import { CautionIcon, CloudIcon } from '@icons'
import { Box, Text } from 'grommet'
import { Close } from 'grommet-icons'
import React, { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { getFileIconSrc } from '../../../../utils/fileIcon'
import { PersistedUploaderFile, type UploaderFile } from '../types'

interface FileUploaderProps {
  onFileUpload: (file: File) => void
  onFileRemove?: (file: File) => void
  files?: UploaderFile[] // Accept both File and persisted
  accept?: Record<string, string[]> // Dynamic accept prop for file types
  // Optional default caution message. If omitted, component uses its built-in message.
  cautionMessage?: string | null
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileUpload,
  onFileRemove,
  files,
  accept,
  cautionMessage: cautionMessageProp
}) => {
  // If files prop is provided, use it as controlled, else use internal state.
  const [internalFiles, setInternalFiles] = useState<File[]>([])
  const uploadedFiles: UploaderFile[] = files !== undefined ? files : internalFiles

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const defaultCaution = 'Please note that the uploaded file should contain the patient’s signature.'
  const initialCaution = cautionMessageProp !== undefined ? cautionMessageProp : defaultCaution
  const [cautionMessage, setCautionMessage] = useState<string | null>(initialCaution)

  useEffect(() => {
    setCautionMessage(cautionMessageProp !== undefined ? cautionMessageProp : defaultCaution)
  }, [cautionMessageProp])

  const onDrop = (acceptedFiles: File[], fileRejections: any[]) => {
    if (fileRejections?.length > 0) {
      const { errors } = fileRejections[0]
      setErrorMessage(errors[0].message)
      return
    }

    // Filter out duplicates by name and size
    const newFiles = acceptedFiles.filter(
      file =>
        !uploadedFiles.some(f =>
          f instanceof File
            ? f.name === file.name && f.size === file.size
            : f.filename === file.name && f.byte_size === file.size
        )
    )
    if (newFiles?.length === 0) return
    if (files === undefined) {
      setInternalFiles([...newFiles])
    }

    setErrorMessage(null)
    setCautionMessage(null)
    newFiles.forEach(file => onFileUpload(file))
  }

  // Default accept types including images
  const defaultAccept = {
    'application/pdf': ['.pdf'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'image/gif': ['.gif'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'text/csv': ['.csv']
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: accept || defaultAccept,
    maxSize: 200 * 1024 * 1024,
    multiple: true,
    onDrop // Allow multiple files
  })

  const handleRemove = (file: UploaderFile) => {
    // Only allow removal of File objects, not persisted
    if (!(file instanceof File)) return
    if (files === undefined) {
      setInternalFiles(prev => prev.filter(f => f !== file))
    }
    onFileRemove && onFileRemove(file)
    if (uploadedFiles.filter(f => f instanceof File)?.length === 1) {
      setCautionMessage(cautionMessageProp !== undefined ? cautionMessageProp : defaultCaution)
      setErrorMessage(null)
    }
  }

  return (
    <Box className="">
      <Box
        align="center"
        gap={'.6em'}
        justify="center"
        {...getRootProps({ className: 'cursor-pointer' })}
        className="dropzone border-dashed border-2  bg-[#E7E7E74F] border-gray-600 py-16 text-center rounded-lg"
      >
        <input {...getInputProps()} />
        <CloudIcon />
        <Text className="text-[#909090]" size="14px">
          Drop files here or{' '}
          <Text className="text-[#5637AE] font-bold" size="14px">
            browse
          </Text>
        </Text>
        <Text className="text-[#909090]" size="14px">
          Max. File size: 200 MB
        </Text>
      </Box>
      {uploadedFiles?.length === 0 && (
        <Box
          className={` items-center gap-x-3 mt-4 py-4 px-5 ${cautionMessage ? 'bg-[#FEFFDB]' : 'bg-red-200'}`}
          direction="row"
        >
          {cautionMessage ? <CautionIcon /> : ''}
          {cautionMessage && (
            <Text color="" size="14px">
              {cautionMessage}
            </Text>
          )}
          {errorMessage && (
            <Text color="" size="14px">
              {errorMessage}
            </Text>
          )}
        </Box>
      )}
      {uploadedFiles?.length > 0 && (
        <Box gap="small" margin={{ top: 'small' }}>
          {uploadedFiles?.map((file, idx) => {
            const isPersisted = !(file instanceof File)
            return (
              <Box
                align="center"
                className="border border-solid border-[#EAECF0] items-center p-3 mt-2"
                direction="row"
                justify="between"
                key={
                  isPersisted
                    ? (file as PersistedUploaderFile)?.id || idx
                    : (file as File).name + (file as File).size + idx
                }
              >
                <Box align="center" direction="row" gap={'1em'}>
                  <img
                    alt="File Icon"
                    className="w-10 h-full "
                    src={getFileIconSrc(
                      isPersisted
                        ? (file as PersistedUploaderFile).content_type || (file as PersistedUploaderFile).filename
                        : (file as File).type || (file as File).name
                    )}
                  />
                  <Box className="text-left py-1">
                    <Text className="font-[500]" size="14px">
                      {isPersisted ? (file as PersistedUploaderFile).filename : (file as File).name}
                    </Text>
                    <Text color={'#909090'} size="14px">
                      {isPersisted
                        ? ((file as PersistedUploaderFile).byte_size / (1024 * 1024)).toFixed(2)
                        : ((file as File).size / (1024 * 1024)).toFixed(2)}{' '}
                      MB
                    </Text>
                  </Box>
                </Box>
                {/* Only allow removal for new files */}
                {!isPersisted && (
                  <Close className="cursor-pointer" color="black" onClick={() => handleRemove(file)} size="small" />
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default FileUploader
